import { Link } from "react-router-dom";
import {
  ShieldCheck, Coffee, PawPrint, Anchor, Sparkles, Heart,
  Waves, Wifi, ParkingSquare, Truck, Utensils, Bed, Battery, Clock,
} from "lucide-react";
import PageHero from "../components/PageHero";
import Breadcrumbs from "../components/Breadcrumbs";
import BookCallCTA from "../components/BookCallCTA";

const AMENITIES = [
  { icon: ShieldCheck, title: "24-hour front desk", body: "A real person, at any hour. The phone at (660) 885-2206 rings straight to us — no automated tree, no offshore call center." },
  { icon: Coffee, title: "Free deluxe hot breakfast", body: "Included with every stay, served fresh in our breakfast room. Hot items, cereals, waffles, pastries, fruit, coffee and juices. Menu rotates; a hot option is always available." },
  { icon: Waves, title: "Seasonal outdoor pool", body: "Open in warm months. Call the desk to confirm current dates before you travel." },
  { icon: Wifi, title: "Free Wi-Fi", body: "Throughout the hotel and in every room." },
  { icon: ParkingSquare, title: "Free parking", body: "Free lot parking for cars, trucks, buses and boat trailers." },
  { icon: PawPrint, title: "Pet friendly (2 pets/room)", body: "Up to two pets welcome per room. A small pet fee applies — call for current amounts. Pets must be leashed on the property and cannot be left unattended in rooms." },
  { icon: Truck, title: "Truck & bus parking + trucker rate", body: "Dedicated pull-through space for work trucks and coaches. Call the desk and mention it's a work run for our trucker rate." },
  { icon: Anchor, title: "Boat parking + BOAT CHARGING ACCESS", body: "Boat and trailer parking is available. Anglers running electric trolling motors and lithium banks — this is the differentiator we hear about most. Ask the desk on arrival for the outlets and expectations for overnight charging." },
  { icon: Utensils, title: "In-room fridge & coffee maker", body: "Every room. Microwave in Queen Double and Jacuzzi Suite; refrigerator only in other rooms — call if microwave matters." },
  { icon: Bed, title: "Euro-top mattresses", body: "In renovated rooms across the property. LED-lit granite baths, 50\" LED TVs with cable and HBO." },
];

const POLICIES = [
  { title: "Check-in / Check-out", body: "Check-in 3:00 PM. Check-out 11:00 AM. Early check-in and late check-out are subject to availability — call and ask." },
  { title: "Kids under 18 stay free", body: "In existing beds. Include them in your reservation so we can make sure the room is set up correctly." },
  { title: "Smoke-free indoors", body: "The entire hotel is smoke-free inside. Designated smoking area outside." },
  { title: "Pets", body: "Up to two pets per room. Small pet fee applies — call (660) 885-2206 for the current amount. Leashed on the property, not left unattended in rooms." },
  { title: "Cancellations", body: "Windows are shown at booking. For flexibility questions on group blocks or extended stays, call the desk." },
  { title: "Quiet hours", body: "10:00 PM to 7:00 AM. Please respect them regardless of shift or trip type." },
  { title: "Accessible-layout Queen room", body: "One accessible-layout Queen room is available. Please call ahead to confirm the specific features you need for your stay — we're happy to talk it through." },
];

export default function AmenitiesPage() {
  return (
    <>
      <PageHero
        eyebrow="Amenities & Policies"
        title="Every amenity we actually have. Nothing invented."
        intro="24-hour desk, free hot breakfast, seasonal outdoor pool, free Wi-Fi and parking, truck/bus parking, and — for the Truman Lake anglers — boat parking and boat charging access."
        image="/images/pool-1.jpg"
        metaTitle="Amenities & Policies — Westbridge Inn & Suites, Clinton MO"
        metaDesc="Full amenities and policies at Westbridge Inn & Suites in Clinton, MO — hot breakfast, pool, Wi-Fi, boat & truck parking, pet friendly, kids stay free, and more."
      />
      <Breadcrumbs items={[{ label: "Amenities" }]} />

      {/* Amenities grid */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-20">
          <div className="max-w-2xl mb-10">
            <div className="wb-eyebrow">All amenities</div>
            <h2 className="wb-h2 mt-3">The list. Verified.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="amenities-grid">
            {AMENITIES.map(({ icon: Icon, title, body }) => (
              <article key={title} className="wb-card p-6 bg-white flex gap-4" data-testid={`amenity-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="w-11 h-11 rounded-sm bg-[#FCEEEA] flex items-center justify-center flex-shrink-0">
                  <Icon className="h-5 w-5 text-[#B94A15]" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-[#1A1A1A]" style={{ fontFamily: "'Nunito', sans-serif" }}>{title}</h3>
                  <p className="wb-body text-sm mt-2">{body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Boat Charging Feature */}
      <section className="bg-[#1A1A1A] text-white">
        <div className="wb-container py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="wb-eyebrow text-[#FF9666]">Truman Lake anglers</div>
            <div className="wb-divider mt-4" />
            <h2 className="wb-h2 text-white mt-6 flex items-center gap-3">
              <Battery className="h-8 w-8 text-[#FF9666]" /> Boat Charging Access.
            </h2>
            <p className="wb-body text-white/85 mt-5">
              Tournament weekends have gotten simpler and harder at the same time — the electronics are better, but the batteries need to charge overnight. We built for that. Ask the desk on arrival and we'll show you the outlets. Lithium banks, deep-cycle trolling motor batteries, sonar packs — bring your charger, we've got the power.
            </p>
            <div className="mt-6">
              <Link to="/truman-lake-fishing" className="wb-btn-primary">Angler guide</Link>
            </div>
          </div>
          <img src="/images/exterior-1.jpg" alt="Westbridge Inn & Suites exterior with parking area" className="w-full aspect-[4/3] object-cover" />
        </div>
      </section>

      {/* Policies */}
      <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]">
        <div className="wb-container py-16 md:py-20">
          <div className="max-w-2xl mb-10">
            <div className="wb-eyebrow">Policies</div>
            <h2 className="wb-h2 mt-3">The straightforward version.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="policies-grid">
            {POLICIES.map((p) => (
              <article key={p.title} className="wb-card p-6 bg-white" data-testid={`policy-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="text-lg font-black text-[#1A1A1A]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                  <Clock className="inline h-4 w-4 mr-2 text-[#B94A15]" />{p.title}
                </h3>
                <p className="wb-body text-sm mt-3">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <BookCallCTA />
    </>
  );
}
